import React from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import {
  WEIGHT_RECIPES,
  applyRecipe,
  colorForScreen,
  computeBucketRanges,
  formatBucketRange,
  screenLabel,
  variantWeightTotal,
} from './appLandingUtils';

export function WeightBar({ variants, screens, height = 18 }) {
  const active = (variants || []).filter((v) => Number(v.weight) > 0);
  const total = variantWeightTotal(active) || 1;

  if (active.length === 0) {
    return (
      <Box
        sx={{
          height,
          borderRadius: 1,
          bgcolor: 'action.hover',
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height,
        borderRadius: 1,
        overflow: 'hidden',
        width: '100%',
        bgcolor: 'action.hover',
      }}
    >
      {active.map((v, index) => (
        <Box
          key={v.screen_key}
          title={`${screenLabel(screens, v.screen_key)} ${v.weight}%`}
          sx={{
            width: `${(Number(v.weight) / total) * 100}%`,
            bgcolor: colorForScreen(v.screen_key, index),
            minWidth: Number(v.weight) > 0 ? 4 : 0,
          }}
        />
      ))}
    </Box>
  );
}

export function WeightEditor({ variants, screens, onChange, disabled }) {
  const total = variantWeightTotal(variants);
  const ranges = computeBucketRanges(variants);
  const valid = total === 100;

  const setWeight = (screenKey, raw) => {
    const parsed = raw === '' ? 0 : Math.max(0, Math.min(100, Math.round(Number(raw) || 0)));
    onChange(
      (variants || []).map((v) =>
        v.screen_key === screenKey ? { ...v, weight: Number.isNaN(parsed) ? 0 : parsed } : v
      )
    );
  };

  const setLink = (screenKey, navigation_link) => {
    onChange(
      (variants || []).map((v) => (v.screen_key === screenKey ? { ...v, navigation_link } : v))
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
        {WEIGHT_RECIPES.map((recipe) => (
          <Button
            key={recipe.label}
            size="small"
            variant="outlined"
            disabled={disabled}
            onClick={() => onChange(applyRecipe(recipe.weights, screens, variants))}
          >
            {recipe.label}
          </Button>
        ))}
      </Box>

      <WeightBar variants={variants} screens={screens} height={22} />

      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 0.75, mb: 1.5, color: valid ? 'success.main' : 'error.main' }}
      >
        Total {total} / 100{valid ? '' : ' — API rejects anything else'}
      </Typography>

      {(variants || []).map((v, index) => {
        const range = ranges.find((r) => r.screen_key === v.screen_key);
        return (
          <Box
            key={v.screen_key}
            sx={{
              display: 'grid',
              gridTemplateColumns: '16px 1fr 88px 1fr',
              gap: 1,
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: colorForScreen(v.screen_key, index),
              }}
            />
            <Typography variant="body2">{screenLabel(screens, v.screen_key)}</Typography>
            <TextField
              size="small"
              type="number"
              label="%"
              value={v.weight}
              disabled={disabled}
              inputProps={{ min: 0, max: 100, step: 1 }}
              onChange={(e) => setWeight(v.screen_key, e.target.value)}
            />
            <TextField
              size="small"
              label="Route override"
              placeholder="optional, e.g. /arcade/browse/fan-wars"
              value={v.navigation_link || ''}
              disabled={disabled}
              onChange={(e) => setLink(v.screen_key, e.target.value)}
            />
            {range ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ gridColumn: '2 / -1', mt: -0.5 }}
              >
                buckets {formatBucketRange(range.range_start, range.range_end)}
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}
